import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import { GlassCard } from '@/components/brand/GlassCard';
import { adminListPayments } from '@/lib/api';
import { formatGBP, formatDate } from '@/lib/utils';

// --- Sub-components ---------------------------------------------------------

const PaymentSearchBar = ({ value, onChange }) => (
    <div className="mb-4">
        <input
            value={value}
            onChange={onChange}
            placeholder="Search by name, email, reference or session id"
            className="w-full max-w-md rounded-la-sm bg-white/[0.04] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-la-gold/40"
            data-testid="admin-payments-search"
        />
    </div>
);

const PaymentRow = ({ payment }) => (
    <tr className="border-t border-white/10 hover:bg-white/[0.03]">
        <td className="px-4 py-3">
            <div className="text-la-text">{payment.metadata?.client_name || '—'}</div>
            <div className="text-[11px] text-la-text3">{payment.metadata?.client_email || '—'}</div>
        </td>
        <td className="px-4 py-3">{payment.metadata?.reference || '—'}</td>
        <td className="px-4 py-3">{formatGBP(payment.amount)}</td>
        <td className="px-4 py-3 capitalize">{payment.payment_status || 'pending'}</td>
        <td className="px-4 py-3 capitalize">{(payment.source || '').replace('_', ' ')}</td>
        <td className="px-4 py-3">{formatDate(payment.created_at)}</td>
        <td className="px-4 py-3 text-[11px] text-la-text3 max-w-[180px] truncate">{payment.session_id}</td>
    </tr>
);

const PAYMENTS_TABLE_HEADERS = [
    'Client', 'Reference', 'Amount', 'Status', 'Source', 'Created', 'Session',
];

const PaymentsTable = ({ rows }) => (
    <GlassCard className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
            <table data-testid="admin-table" className="w-full text-sm">
                <thead className="bg-white/[0.04]">
                    <tr className="text-[11px] tracking-[0.14em] uppercase text-la-text3">
                        {PAYMENTS_TABLE_HEADERS.map((h) => (
                            <th key={h} className="text-left px-4 py-3">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={PAYMENTS_TABLE_HEADERS.length} className="px-4 py-10 text-center text-la-text3">No payments.</td></tr>
                    ) : rows.map((p) => <PaymentRow key={p.session_id} payment={p} />)}
                </tbody>
            </table>
        </div>
    </GlassCard>
);

// Helper: filter payments by free-text query (name / email / reference / session id).
const matchesQuery = (payment, query) => {
    if (!query) return true;
    const haystack = [
        payment.metadata?.client_email || '',
        payment.metadata?.client_name || '',
        payment.metadata?.reference || '',
        payment.session_id || '',
    ].join(' ').toLowerCase();
    return haystack.includes(query.toLowerCase());
};

// --- Main page --------------------------------------------------------------

export default function AdminPaymentsPage() {
    const [items, setItems] = useState([]);
    const [query, setQuery] = useState('');

    useEffect(() => {
        let mounted = true;
        adminListPayments()
            .then((data) => { if (mounted) setItems(data); })
            .catch(() => { /* surfaced via toast in a future enhancement */ });
        return () => { mounted = false; };
    }, []);

    const onSearchChange = useCallback((e) => setQuery(e.target.value), []);

    const filtered = useMemo(
        () => items.filter((p) => matchesQuery(p, query)),
        [items, query],
    );

    return (
        <AdminLayout title="Payments">
            <PaymentSearchBar value={query} onChange={onSearchChange} />
            <PaymentsTable rows={filtered} />
        </AdminLayout>
    );
}
